import React, { useCallback } from 'react';
import { Dialog, InputLabel, Box } from '@mui/material';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { COUNTRY_OPTIONS } from '../../constants';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { AppRow } from '../../layout/appRow';
import { AppButton } from '../app/button/appButton';
import { AppControlledSwitch } from '../app/input/appControlledSwitch';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import {
  AppControlledCalendar,
  InputFormat,
} from '../app/appControlledCalendar';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  handleAddHoliday: SubmitHandler<FieldValues>;
}

export default function AddHolidayDialog({
  isOpen,
  handleClose,
  handleAddHoliday,
}: Props): JSX.Element {
  const methods = useForm();

  const handleSubmitForm = useCallback(
    (data) => {
      handleAddHoliday(data);
      methods.reset();
    },
    [handleAddHoliday, methods]
  );

  const closeModal = useCallback(() => {
    methods.reset();
    handleClose();
  }, [handleClose, methods]);

  return (
    <Dialog
      open={isOpen}
      PaperProps={{
        style: {
          margin: 0,
        },
      }}
    >
      <AppDialogTitle title="Add holiday" handleClose={closeModal} />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmitForm)}>
          <Box sx={{ pl: 4, pr: 4, pt: 4 }}>
            <AppRow sx={{ mb: 3 }}>
              <InputLabel>Date</InputLabel>
              <AppControlledCalendar
                name="dayOffDate"
                inputFormat={InputFormat.SEPARATOR_SPACE}
                defaultValue={new Date()}
                sx={{ width: '160px' }}
              />
            </AppRow>
            <AppRow sx={{ mb: 3 }}>
              <AppControlledAutocomplete
                label="Country"
                name="country"
                options={COUNTRY_OPTIONS}
                sx={{ width: '100%' }}
                isRequired
                isError={!!methods.formState.errors.country}
              />
            </AppRow>
            <AppRow sx={{ mb: 3 }}>
              <AppControlledTextField
                name="name"
                label="Official Name"
                sx={{ width: '100%' }}
                defaultValue=""
                isRequired
                isError={!!methods.formState.errors.name}
              />
            </AppRow>
            <AppRow sx={{ mb: 3 }}>
              <InputLabel>Is date fixed?</InputLabel>
              <Box width={70}>
                <AppControlledSwitch name="isDateFixed" />
              </Box>
            </AppRow>
          </Box>
          <AppRow>
            <AppButton label="Cancel" onClick={closeModal} />
            <AppButton label="Save" isPrimary buttonType="submit" />
          </AppRow>
        </form>
      </FormProvider>
    </Dialog>
  );
}
